import { AddToCart } from "./elements/cart/AddToCart";
import type { BillingCheckbox } from "./elements/checkout/BillingAddressCheckbox";
import { implementSearchBar } from "./elements/SearchResult";
import { noticeFailure } from "./notices";

/* DO NOT BOTHER with any regex more complicated than this. The only true way to
 * check if an email address is valid is to try sending an email.
 */
const EMAIL_REGEX = /^[^@]+@[^@]+\.[^@]+$/;

export type ObservedAttributes<C extends { observedAttributes: readonly string[] }> = C["observedAttributes"] extends readonly (infer T)[] ? T : never;

export const LOCAL_PICKUP_FIELD_NAMES = [
	"shipping_first_name",
	"shipping_last_name",
	"shipping_company",
	"shipping_phone",
];

export function udpdateReview() {
	const checkoutDeliveryElement = document.getElementById("checkout-delivery") as HTMLElement;
	const checkoutBillingElement = document.getElementById("checkout-billing") as HTMLElement;

	const shippingOption = (document.querySelector('input[name="shipping_method[0]"]:checked') as HTMLInputElement).id as "shipping-option" | "pickup-option";
	const deliveryInputs = checkoutDeliveryElement.querySelectorAll("select, input") as NodeListOf<HTMLInputElement | HTMLSelectElement>;
	const billingInputs = checkoutBillingElement.querySelectorAll("select, input") as NodeListOf<HTMLInputElement | HTMLSelectElement>;

	const billingCheckbox = document.querySelector<BillingCheckbox>("gct-billing-address-checkbox");
	const isBillingAddressSameAsDelivery = billingCheckbox?.checked ?? false;

	const reviewEmail = document.getElementById("email-review")!;
	const reviewDelivery = document.getElementById("delivery-info-review")!;
	const reviewDeliveryOption = document.getElementById("delivery-option-review")!;
	const reviewBilling = document.getElementById("billing-info-review")!;
	const reviewPayment = document.getElementById("payment-method-review")!;

	reviewEmail.textContent = (document.getElementById("billing_email") as HTMLInputElement).value;

	reviewDelivery.replaceChildren(...Array.from(deliveryInputs)
		.filter((input) => input.value.length > 0 && (shippingOption !== "pickup-option" || LOCAL_PICKUP_FIELD_NAMES.includes(input.name)))
		.map((input) => <div className="gct-text-lg">{inputDisplayValue(input)}</div>)
	);

	reviewDeliveryOption.textContent = shippingOption === "pickup-option" ? "Pick up" : "Delivery";

	if (isBillingAddressSameAsDelivery) {
		reviewBilling.replaceChildren(
			<div className="gct-text-lg"><em>Same as delivery</em></div>
		);
	} else {
		reviewBilling.replaceChildren(...Array.from(billingInputs)
			.filter((input) => input.value.length > 0)
			.map((input) => <div className="gct-text-lg">{inputDisplayValue(input)}</div>)
		);
	}

	const paymentOptionElemnt = document.querySelector<HTMLInputElement>('input[name="payment_method"]:checked');
	const paymentLabel = document.querySelector(`label[for="payment_method_${paymentOptionElemnt?.value ?? ""}"]`);

	if (paymentLabel !== null) {
		reviewPayment.innerHTML = paymentLabel.innerHTML;
	} else {
		// Payment lable does not exist when there is only one payment option
		(reviewPayment.parentNode as HTMLElement).classList.add("gct-d-none");
	}
}

function validateRegex(input: HTMLInputElement | HTMLSelectElement, regex: RegExp): boolean {
	if (!regex.test(input.value)) {
		input.classList.add("gct-input-error");
		return false;
	} else {
		input.classList.remove("gct-input-error");
		return true;
	}
}

function validateNonEmpty(input: HTMLInputElement | HTMLSelectElement): boolean {
	if (input.value.length === 0) {
		input.classList.add("gct-input-error");
		return false;
	} else {
		input.classList.remove("gct-input-error");
		return true;
	}
}

function inputDisplayValue(input: HTMLInputElement | HTMLSelectElement): string {
	if (input instanceof HTMLSelectElement) {
		const option = input.selectedOptions[0];
		return option?.textContent ?? "";
	}

	return input.value;
}

export function validateDeliveryForm() {
	const checkoutDeliveryElement = document.getElementById("checkout-delivery") as HTMLElement;

	const emailInput = document.getElementById("billing_email") as HTMLInputElement;
	const deliveryInputs = checkoutDeliveryElement.querySelectorAll("select, input") as NodeListOf<HTMLInputElement | HTMLSelectElement>;
	const isLocalPickup = (document.querySelector('input[name="shipping_method[0]"]:checked') as HTMLInputElement).id === "pickup-option";

	let isFormValid = validateRegex(emailInput, EMAIL_REGEX);
	for (const input of deliveryInputs) {
		if ("required" in input.dataset && (LOCAL_PICKUP_FIELD_NAMES.includes(input.name) || !isLocalPickup)) {
			/* DO NOT INLINE THIS VARIABLE
			 * If you inline `valid`, the computation will short-circuit (by
			 * definition of the && operator) and only the first invalid input
			 * will get a red border.
			 */
			const valid = validateNonEmpty(input);
			isFormValid &&= valid;
		}
	}

	if (!isFormValid) {
		noticeFailure("Please provide all the necessary details");
	}

	return isFormValid;
}

export function validateBillingForm() {
	const billingCheckbox = document.querySelector<BillingCheckbox>("gct-billing-address-checkbox");
	const isBillingAddressSameAsDelivery = billingCheckbox?.checked ?? false;

	if (isBillingAddressSameAsDelivery) {
		return true;
	}

	const checkoutBillingElement = document.getElementById("checkout-billing") as HTMLElement;
	const billingInputs = checkoutBillingElement.querySelectorAll("select, input") as NodeListOf<HTMLInputElement | HTMLSelectElement>;

	let isFormValid = true;
	for (const input of billingInputs) {
		if ("required" in input.dataset) {
			/* DO NOT INLINE THIS VARIABLE
			 * If you inline `valid`, the computation will short-circuit (by
			 * definition of the && operator) and only the first invalid input
			 * will get a red border.
			 */
			const valid = validateNonEmpty(input);
			isFormValid &&= valid;
		}
	}

	if (!isFormValid) {
		noticeFailure("Please provide all the necessary details");
	}

	return isFormValid;
}

export function implementArrowNavigation() {
	const prevButton = document.getElementById("carousel-arrow-prev");
	const nextButton = document.getElementById("carousel-arrow-next");

	function handleCarouselMoveNext() {
		const carousel = document.getElementById("cross-sells-container")
		const slide = document.querySelector(".gct-cross-sell");

		const slideWidth = slide!.clientWidth;
		carousel!.scrollLeft = carousel!.scrollLeft + slideWidth;
		(prevButton as HTMLButtonElement).disabled = false;

		const maxScrollLeft = carousel!.scrollWidth - carousel!.clientWidth;
		if (carousel!.scrollLeft >= maxScrollLeft) {
			(nextButton as HTMLButtonElement).disabled = true;
		}

	}

	function handleCarouselMovePrev() {
		const carousel = document.getElementById("cross-sells-container")
		const slide = document.querySelector(".gct-cross-sell");
		const slideWidth = slide!.clientWidth;
		carousel!.scrollLeft = carousel!.scrollLeft - slideWidth;
		(nextButton as HTMLButtonElement).disabled = false;

		if (carousel!.scrollLeft <= 0) {
			(prevButton as HTMLButtonElement).disabled = true;
		}
	}

	prevButton?.addEventListener("click", handleCarouselMovePrev);
	nextButton?.addEventListener("click", handleCarouselMoveNext);
};

export function implementArrowNavigation_gen(carouselId: string, slideId: string, prveButtonId: string, nextButtonId: string) {
	const prevButton = document.getElementById(prveButtonId);
	const nextButton = document.getElementById(nextButtonId);

	function handleCarouselMoveNext() {
		const carousel = document.getElementById(carouselId)
		const slide = document.querySelector(slideId);
		const slideWidth = slide!.clientWidth;
		carousel!.scrollLeft = carousel!.scrollLeft + slideWidth;
		(prevButton as HTMLButtonElement).disabled = false;

		const maxScrollLeft = carousel!.scrollWidth - carousel!.clientWidth;
		if (carousel!.scrollLeft >= maxScrollLeft) {
			(nextButton as HTMLButtonElement).disabled = true;
		}

	}

	function handleCarouselMovePrev() {
		const carousel = document.getElementById(carouselId)
		const slide = document.querySelector(slideId);
		const slideWidth = slide!.clientWidth;
		carousel!.scrollLeft = carousel!.scrollLeft - slideWidth;
		(nextButton as HTMLButtonElement).disabled = false;

		if (carousel!.scrollLeft <= 0) {
			(prevButton as HTMLButtonElement).disabled = true;
		}
	}

	prevButton?.addEventListener("click", handleCarouselMovePrev);
	nextButton?.addEventListener("click", handleCarouselMoveNext);
};

export function collectionNavigation() {
	const prevButton = document.getElementById("collection-carousel-arrow-prev");
	const nextButton = document.getElementById("collection-carousel-arrow-next");

	let scrollControll = 0;

	function handleCarouselMoveNext() {
		const carousel = document.getElementById("collections-names-container")

		carousel!.scrollLeft = carousel!.scrollLeft + 100;
		scrollControll += 100;
		(prevButton as HTMLButtonElement).disabled = false;

		if (carousel!.scrollLeft < scrollControll) {
			(nextButton as HTMLButtonElement).disabled = true;
		}

	}

	function handleCarouselMovePrev() {
		const carousel = document.getElementById("collections-names-container")

		carousel!.scrollLeft = carousel!.scrollLeft - 100;
		scrollControll -= 100;
		(nextButton as HTMLButtonElement).disabled = false;

		if (carousel!.scrollLeft <= 0) {
			(prevButton as HTMLButtonElement).disabled = true;
		}
	}

	prevButton?.addEventListener("click", handleCarouselMovePrev);
	nextButton?.addEventListener("click", handleCarouselMoveNext);
};

export function showMinicart() {
	const minicart = document.getElementById("mini-cart-container");
	const overlay = document.getElementById("overlay");

	const basketButton = document.getElementById("header-basket-button");
	const isCheckout = basketButton?.dataset.isCheckout;
	const cartUrl = basketButton?.dataset.cartUrl;

	if (isCheckout) {
		if (cartUrl && basketButton) {
			window.location.href = cartUrl;
		}
	} else {
		if (minicart) {
			minicart.classList.remove("gct-header-mini-basket");
		}

		if (overlay) {
			overlay.style.zIndex = "110";
			overlay.classList.remove("gct-overlay-hidden");
		}

		localStorage.setItem("showMiniBasket", "true");
	}

}

export function myFunction(containerID: string, buttonId: string) {
	const x = document.getElementById(containerID);
	const button = document.getElementById(buttonId);

	function myFunction() {

		if (!x) return;
		if (!button) return;


		if (x.style.display === "flex") {
			button.style.transform = "rotate(0deg)";
			x.style.display = "none";

		} else {
			button.style.transform = "rotate(180deg)";
			x.style.display = "flex";
		}
	}


	button?.addEventListener("click", myFunction);
}

export function hideMinicart() {
	const minicart = document.getElementById("mini-cart-container");
	const overlay = document.getElementById("overlay");

	if (minicart) {
		minicart.classList.add("gct-header-mini-basket");
	}

	if (overlay) {
		overlay.classList.add("gct-overlay-hidden");
		overlay.style.zIndex = "90";
	}
}

export function implementMiniCart() {
	const basketButton = document.getElementById("header-basket-button");
	const isCart = basketButton?.dataset.isCart;

	if (basketButton && !isCart) {
		basketButton.addEventListener('click', showMinicart);
	}
}

export function updateDisplay() {
	implementArrowNavigation();
	implementSearchBar();
	implementMiniCart();
	implementVariationSelection();
}

/**
 * Generates a new, unique ID suitable for `id` attribute of an HTML element.
 */
export const genId = (() => {
	let nextId = 0;
	return (): string => {
		const id = `gct-gen-${nextId}`;
		nextId += 1;
		return id;
	};
})();

export function headerCustomBehaviour() {
	const header = document.getElementById("header");
	const isFrontPage = header?.getAttribute("data-is-front-page") === "true";

	if (isFrontPage) {
		header.classList.add("header-fornt-page");

		document.addEventListener("scroll", () => {
			if (window.scrollY > 0) {
				header.classList.remove("header-fornt-page");
			} else {
				header.classList.add("header-fornt-page");
			}
		});
	}
}

export function toggleMobileHeader() {
	const burgerButton = document.getElementById('burger');
	const mobileHeaderContainer = document.getElementById("burger-content") as HTMLElement;
	const showHeaderButtons = document.getElementsByName('show-header');
	const mobileHeader = document.querySelector(".gct-header-mobile") as HTMLElement;
	const mobileContainers = document.querySelectorAll<HTMLElement>(".gct-header-mobile-container");
	const submenuButtons = document.querySelectorAll<HTMLElement>("[data-mobile-submenu]");
	const prevHeaderBtns = document.getElementsByName("prev-header-first");

	async function onClickMobileMenu() {
		const inputMobileMenu = document.getElementById("product-search-mobile-menu");
		const searchResultMobileMenu = document.getElementById("search-results-mobile-menu");
		if (inputMobileMenu) (inputMobileMenu as HTMLInputElement).value = "";
		if (searchResultMobileMenu) searchResultMobileMenu.innerHTML = "";
	}

	if (burgerButton && mobileHeaderContainer && mobileHeader) {

		burgerButton.addEventListener('click', () => {
			mobileHeader.classList.add("gct-d-none");
			document.documentElement.classList.add("no-scroll");
			mobileHeaderContainer.classList.remove("gct-d-none");
		});

		for (const btn of submenuButtons) {
			const submenu = document.getElementById(btn.dataset.mobileSubmenu ?? "");
			if (!submenu) continue;

			btn.addEventListener("click", () => {
				mobileHeaderContainer.classList.add("gct-d-none");
				submenu.classList.remove("gct-d-none");
			});
		}

		for (const btn of showHeaderButtons) {
			btn.addEventListener("click", () => {
				for (const container of mobileContainers) {
					container.classList.add("gct-d-none");
				}
				mobileHeader.classList.remove("gct-d-none");
				document.documentElement.classList.remove("no-scroll");
				onClickMobileMenu();
			})
		}

		for (const prevBtn of prevHeaderBtns) {
			prevBtn.addEventListener("click", () => {
				for (const container of mobileContainers) {
					container.classList.add("gct-d-none");
				}
				mobileHeaderContainer.classList.remove("gct-d-none");
			})
		}

	}
}

interface ProductVariation {
	readonly attributes: Readonly<Record<string, string>>;
	readonly price_html: string;
	readonly sku: string;
	readonly variation_id: number;
	readonly image: ProductVariationImage;
}

interface ProductVariationImage {
	/** Empty string when no image. */
	readonly src: string;
	readonly srcset: string;
	readonly sizes: string;
}

/* TODO This is very volatile. Changing the layout on a single product page
 * could break the code below and you wouldn't event notice without checking a
 * variable product.
 */
function implementVariationSelection() {
	const productVariationsElement = document.getElementById("product-variations");
	if (productVariationsElement === null) return;

	const productVariations = JSON.parse(productVariationsElement.textContent!) as readonly ProductVariation[];

	const largeImageElement = document.querySelector("#gct-sp-large-gallery-image img") as HTMLImageElement;
	const carouselImageElement = document.querySelector("#gct-sp-gallery-images img") as HTMLImageElement;

	const productSkuElement = document.getElementById("sku") as HTMLElement;
	const productPriceElement = document.getElementById("gct-sp-product-price") as HTMLElement;
	const addToCartElement = document.querySelector("gct-add-to-cart") as AddToCart;

	const defaultSku = productSkuElement.textContent;
	const defaultPriceHtml = productPriceElement.innerHTML;

	const defaultLargeImageData = {
		src: largeImageElement.src,
		srcset: largeImageElement.srcset,
		sizes: largeImageElement.sizes,
	};

	const defaultCarouselImageData = {
		src: carouselImageElement.src,
		srcset: carouselImageElement.srcset,
		sizes: carouselImageElement.sizes,
	};

	const attributeSelectElements = Array.from(document.querySelectorAll("select[data-attribute_name]")) as readonly HTMLSelectElement[];

	const getAttributeSet = (): Readonly<Record<string, string>> => Object.freeze(Object.fromEntries(
		attributeSelectElements.map((attributeSelect) => [
			attributeSelect.dataset["attribute_name"]!,
			attributeSelect.value,
		] as const),
	));

	const findVariation = (attributeSet: Readonly<Record<string, string>>): ProductVariation | undefined => {
		const ka = Object.keys(attributeSet);
		for (const productVariation of productVariations) {
			const kb = Object.keys(productVariation.attributes);
			if (ka.length === kb.length && ka.every((k) => productVariation.attributes[k] === attributeSet[k])) {
				return productVariation;
			}
		}

		return undefined;
	};

	const onAttributeSelect = function () {
		const attributeSet = getAttributeSet();

		largeImageElement.removeAttribute("srcset");
		largeImageElement.removeAttribute("sizes");

		if (Object.values(attributeSet).some((value) => value === "")) {
			// Not all attributes are selected.
			productSkuElement.textContent = defaultSku;
			productPriceElement.innerHTML = defaultPriceHtml;
			addToCartElement.setAttribute("disabled", "");
			delete addToCartElement.dataset.variationId;
			Object.assign(largeImageElement, defaultLargeImageData);
			Object.assign(carouselImageElement, defaultCarouselImageData);
			return;
		}

		const productVariation = findVariation(attributeSet);

		if (productVariation === undefined) {
			// All attributes are selected, but no variation exists.
			productSkuElement.textContent = "N/A";
			productPriceElement.innerHTML = "N/A";
			addToCartElement.setAttribute("disabled", "");
			delete addToCartElement.dataset.variationId;
			Object.assign(largeImageElement, defaultLargeImageData);
			Object.assign(carouselImageElement, defaultCarouselImageData);

		} else {
			productSkuElement.textContent = productVariation.sku;
			productPriceElement.innerHTML = productVariation.price_html || defaultPriceHtml;
			addToCartElement.removeAttribute("disabled");
			addToCartElement.dataset.variationId = String(productVariation.variation_id);

			if (productVariation.image.src) {
				Object.assign(largeImageElement, productVariation.image);
				Object.assign(carouselImageElement, productVariation.image);
			} else {
				Object.assign(largeImageElement, defaultLargeImageData);
				Object.assign(carouselImageElement, defaultCarouselImageData);
			}
		}
	};

	attributeSelectElements.forEach((e) => e.addEventListener("change", onAttributeSelect));
	onAttributeSelect();
}

export const refreshFgfNotices = () => {
	document
		.querySelectorAll<HTMLParagraphElement>(".fgf-success-info .fgf-notice-content p")
		.forEach((p) => {
			const br = p.querySelector("br");

			// Remove the first text node before <br>
			const firstChild = p.firstChild;
			if (firstChild && firstChild.nodeType === Node.TEXT_NODE) {
				firstChild.remove();
			}

			// Remove the <br>
			if (br) {
				br.remove();
			}
		});

	const first = document.querySelector<HTMLElement>(".fgf-success-info.fgf-notice");
	if (first) {
		first.classList.add("is-first-fgf-success-info");
	}
};
